import { Component, computed, inject, input, OnInit } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { translate } from "../../../../../../shared/translate/translate";
import { LamellaSet } from "../../../../../../shared/product-card/product-card/lamel/lamella-set.class";
import { availableProductsStore } from "../../../../../../shared/store/available-products.store";
import { LamellaListingItem } from "../../../../../../shared/types/lamella-listing-item.type";
import { v4 as uuidv4 } from "uuid";
import { LamelareaImpl } from "../../../../../../shared/product-card/product-card/lamelarea.class";
import { DecimalPipe } from "@angular/common";
import { LamelareaStore } from "../../../../../../shared/product-card/product-card/lamelarea.store";


interface CalculatedLamellaSet {
    id: string;
  name: string;
  material: string;
  color: string;
  rows: number;
  total: number;
}

@Component({
  selector: 'app-quantity-lamella-area',
  standalone: true,
  imports: [TranslatePipe, DecimalPipe],
  templateUrl: "./quantity-lamella-area.component.html",
  styleUrl: "./quantity-lamella-area.component.scss",
})
export class QuantityLamellaAreaComponent implements OnInit {
  /* -------------------------------------------------------------
   *  Template helpers & stores
   * ----------------------------------------------------------- */
  protected readonly translate = translate;
  protected readonly Math = Math;

  private readonly availableProductStore = inject(availableProductsStore);
  protected readonly lamellaAreaStore = inject(LamelareaStore);

  /* -------------------------------------------------------------
   *  Inputs
   * ----------------------------------------------------------- */
  /** **ID** of the lamella area */
  readonly lamelareaId = input.required<string>();
  /** Lamella‑alueen malli (LamelareaImpl) */
  readonly lamellaArea = input.required<LamelareaImpl>();
  /** Page‑break printtinäkymässä */
  readonly pageBreak = input<boolean>(false);

  /* -------------------------------------------------------------
   *  Reactive state
   * ----------------------------------------------------------- */
  private calculatedLamellaSets: CalculatedLamellaSet[] = [];

  /**
   * Näytettävät rivit mallille – luodaan, kun määrälaskenta on valmis.
   * Käytetään alkuperäisen setin indeksiä hakemaan tuotekuvan & kategorian.
   */
  protected lamellaSetsResults = computed(() => {

    if (!this.calculatedLamellaSets.length) return [] as any[];

    return this.lamellaArea().lamellaSets.map((set: LamellaSet, i: number) => {
      const stats = this.calculatedLamellaSets[i];
      const product = this.getLamellaProduct(set);
      return {
        id: stats?.id ?? uuidv4(),
        image: product.coverImage,
        productFamily: product.lamellaCategory,
        name: stats?.name ?? set.name,
        material: stats?.material ?? set.material,
        color: set.color,
        amount: stats?.total ?? 0,
      } as const;
    });
  });

  /* -------------------------------------------------------------
   *  Lifecycle
   * ----------------------------------------------------------- */
  ngOnInit(): void {
    // Lasketaan määrät heti komponentin alustuksessa
    this.calculatedLamellaSets = this.calculateAllLamellaSets(this.lamellaArea());
  }

  /* -------------------------------------------------------------
   *  Public helpers
   * ----------------------------------------------------------- */
  private getLamellaProduct(set: LamellaSet): LamellaListingItem {
    return this.availableProductStore.findFromAvailableProducts(set.productCode);
  }

  /* -------------------------------------------------------------
   *  Core quantity calculation – sama logiikka kuin insertLamellaSets
   * ----------------------------------------------------------- */
  private calculateAllLamellaSets(area: Readonly<LamelareaImpl>): CalculatedLamellaSet[] {
    /* 0) Alueparametrit */
    const areaHeight = area.orientation === 'horizontal' ? area.height : area.width;
    const lamellaLength = area.orientation === 'horizontal' ? area.width : area.height;

    /* 1) Tuotetut setit suoraan listaan (ei wrapperia) */
    const producedSets: LamellaSet[] = [];

    let usedHeight = 0;
    let lastTop = 0;
    let prev: LamellaSet | null = null;

    /* 2) Pääsilmukka – identtinen aiempaan */
    outer: while (usedHeight < areaHeight) {
      for (const current of area.lamellaSets) {

        const productChanged = prev && prev.productCode !== current.productCode;
        if (productChanged) {
          usedHeight = this.addGapIfNeededMm(usedHeight, lastTop, current.spaceAfter);
        }

        const unit = current.lamellaSpace + current.lamellaSpaceBetween;
        const available = areaHeight - usedHeight;
        const potential = Math.floor((available + current.lamellaSpaceBetween) / unit);
        const repeats =
            current.lamellaRepeat === Infinity
                ? potential
                : Math.min(current.lamellaRepeat, potential);

        if (repeats === 0) break outer;

        const copy: LamellaSet = { ...current, lamellaRepeat: repeats };
        producedSets.push(copy);

        usedHeight += unit * repeats;
        lastTop = usedHeight - current.lamellaSpaceBetween;

        if (productChanged) {
          usedHeight = this.addGapIfNeededMm(usedHeight, lastTop, current.spaceBefore);
        }

        prev = copy;
        if (usedHeight >= areaHeight) break outer;
      }
    }

    /* 3) Ryhmittely Mapilla – O(n) */
    const grouped = new Map<string, CalculatedLamellaSet>();

    for (const set of producedSets) {
      const key = set.id?.toString() ?? set.productCode ?? set.name ?? 'Unknown';
      const rows = set.lamellaRepeat;
      const total = lamellaLength * rows;

      const entry =
          grouped.get(key) ??
          {
            id: key,
            name: set.name ?? set.productCode ?? 'Unknown',
            material: set.material,
            color: set.color?.HEX ?? 'N/A',
            rows: 0,
            total: 0,
          };

      entry.rows += rows;
      entry.total += total;
      grouped.set(key, entry);
    }

    return [...grouped.values()];
  }

  /**
   * addGapIfNeededMm – vastine insertLamellaSets.addGapIfNeeded, millimetreinä.
   *
   * @param used           Tähän asti käytetty korkeus
   * @param lastTop        Viimeisen säleen yläpinnan korkeus
   * @param requiredGap    Tarvittava gap mm‑yksiköissä
   * @returns              Mahdollisesti päivitetty used‑arvo
   */
  private addGapIfNeededMm(
      used: number,
      lastTop: number,
      requiredGap: number
  ): number {
    if (!requiredGap) return used;
    const existingGap = used - lastTop;
    return requiredGap > existingGap ? used + (requiredGap - existingGap) : used;
  }

  /* Expose class reference to template (static access) */
  protected readonly LamelareaStore = LamelareaStore;
}
